import { type Project } from "@/lib/projectStore";
import {
  PenTool, GitBranch, Columns3, CalendarDays, FileText, Users,
  ClipboardList, ShieldCheck, FlaskConical, TestTube2, Zap,
  PackageOpen, BookMarked, Wrench, Table2, GraduationCap,
  BookOpen, Bot, TrendingUp, BarChart3, Sparkles, BriefcaseBusiness,
  Truck, CalendarRange, ChevronRight,
} from "lucide-react";

// Neumorphism — matches CompliancePanel
const nm = {
  base:   "bg-[#e4ecfb]",
  raised: "bg-[#e4ecfb] shadow-[5px_5px_12px_#bcc8e0,-5px_-5px_12px_#ffffff]",
  inset:  "bg-[#e4ecfb] shadow-[inset_4px_4px_8px_#bcc8e0,inset_-4px_-4px_8px_#ffffff]",
  card:   "bg-[#e4ecfb] shadow-[4px_4px_10px_#bcc8e0,-4px_-4px_10px_#ffffff]",
};

type Tab =
  | "diagram" | "stories" | "kanban" | "timeline" | "velocity" | "analysis"
  | "documents" | "stakeholders" | "requirements" | "compliance" | "agent-log"
  | "knowledge-base" | "team" | "schedule" | "sharing" | "activity" | "test-cases" | "test-runner"
  | "impact-analysis" | "case-studies" | "genius-labs" | "handover"
  | "job-intelligence" | "evolution-log" | "capa" | "suppliers" | "traceability"
  | "decisions" | "risks" | "meeting-notes" | "sprints" | "okr" | "brain" | "catch-up"
  | "charter" | "budget-evm" | "wbs" | "critical-path"
  | "gap-analysis" | "data-dictionary" | "use-cases" | "roadmap";

interface FlowTab {
  id: Tab;
  label: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  getCount?: (p: Project) => number | null;
}

interface Stage {
  id: string;
  label: string;
  description: string;
  color: string;         // hex for inline use
  colorLight: string;    // light tint for badge bg
  tabs: FlowTab[];
}

const STAGES: Stage[] = [
  {
    id: "define", label: "Define", description: "What & who",
    color: "#2563eb", colorLight: "#dbeafe",
    tabs: [
      { id: "requirements", label: "Requirements", icon: ClipboardList },
      { id: "stakeholders",  label: "Stakeholders",  icon: Users,     getCount: (p) => p.stakeholders?.length ?? null },
      { id: "compliance",    label: "Compliance",    icon: ShieldCheck },
    ],
  },
  {
    id: "plan", label: "Plan", description: "Structure & scope",
    color: "#7c3aed", colorLight: "#ede9fe",
    tabs: [
      { id: "diagram",   label: "Diagram",      icon: PenTool,   getCount: (p) => p.diagramText?.trim().length ? 1 : 0 },
      { id: "documents", label: "Documents",    icon: FileText,  getCount: (p) => p.documents?.length ?? null },
      { id: "stories",   label: "User Stories", icon: GitBranch, getCount: (p) => p.userStories?.length ?? null },
    ],
  },
  {
    id: "build", label: "Build", description: "Execute & track",
    color: "#d97706", colorLight: "#fef3c7",
    tabs: [
      { id: "kanban",   label: "Board",           icon: Columns3,    getCount: (p) => p.userStories?.filter(s => s.status !== "backlog").length ?? null },
      { id: "timeline", label: "Timeline",        icon: CalendarDays, getCount: (p) => p.userStories?.filter(s => s.dueDate).length ?? null },
      { id: "team",     label: "Team & Schedule", icon: Users },
    ],
  },
  {
    id: "verify", label: "Verify", description: "Test & validate",
    color: "#ea580c", colorLight: "#ffedd5",
    tabs: [
      { id: "test-cases",      label: "Test Cases",      icon: FlaskConical },
      { id: "test-runner",     label: "Test Runner",     icon: TestTube2 },
      { id: "impact-analysis", label: "Impact Analysis", icon: Zap },
    ],
  },
  {
    id: "quality", label: "Quality", description: "Improve & trace",
    color: "#e11d48", colorLight: "#ffe4e6",
    tabs: [
      { id: "capa",          label: "CAPA",          icon: Wrench },
      { id: "traceability",  label: "Traceability",  icon: Table2 },
      { id: "evolution-log", label: "Evolution Log", icon: BookMarked },
    ],
  },
  {
    id: "deliver", label: "Deliver", description: "Ship & hand off",
    color: "#059669", colorLight: "#d1fae5",
    tabs: [
      { id: "handover",       label: "Handover",       icon: PackageOpen },
      { id: "case-studies",   label: "Case Studies",   icon: GraduationCap },
      { id: "knowledge-base", label: "Knowledge Base", icon: BookOpen },
    ],
  },
];

const SUPPORT: FlowTab[] = [
  { id: "velocity",        label: "Velocity",   icon: TrendingUp,       getCount: (p) => p.userStories?.filter(s => s.status === "done").length ?? null },
  { id: "analysis",        label: "Analysis",   icon: BarChart3,        getCount: (p) => p.analyses?.length ?? null },
  { id: "agent-log",       label: "Agent Log",  icon: Bot },
  { id: "suppliers",       label: "Suppliers",  icon: Truck },
  { id: "genius-labs",     label: "Genius Labs",icon: Sparkles },
  { id: "job-intelligence",label: "Job Intel",  icon: BriefcaseBusiness },
  { id: "schedule",        label: "Schedule",   icon: CalendarRange },
];

function stageStatus(stage: Stage, project: Project): "has-data" | "empty" | "unknown" {
  const results = stage.tabs.filter(t => t.getCount).map(t => t.getCount!(project));
  if (results.length === 0) return "unknown";
  const known = results.filter(r => r !== null) as number[];
  if (known.length === 0) return "unknown";
  return known.some(n => n > 0) ? "has-data" : "empty";
}

interface Props {
  project: Project;
  activeTab: Tab;
  onNavigate: (tab: Tab) => void;
}

export default function ProjectFlowMap({ project, activeTab, onNavigate }: Props) {
  return (
    <div className={`min-h-full ${nm.base} p-5 overflow-auto`}>

      {/* Header */}
      <div className={`${nm.raised} rounded-xl px-4 py-3 mb-5 flex items-center gap-3`}>
        <div className="rounded-lg p-1.5" style={{ backgroundColor: "#2d5bbf" }}>
          <ChevronRight className="h-4 w-4 text-white" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-[#111827]">Project Flow</h2>
          <p className="text-[10px] text-[#6b7280]">Click any stage to open it · green dot = has data · red arrow = stage not started</p>
        </div>
      </div>

      {/* Stage pipeline */}
      <div className="flex items-start gap-0 overflow-x-auto pb-4">
        {STAGES.map((stage, i) => {
          const status = stageStatus(stage, project);
          const prevBlocked = i > 0 && stageStatus(STAGES[i - 1], project) === "empty";

          return (
            <div key={stage.id} className="flex items-start shrink-0">
              {/* Arrow */}
              {i > 0 && (
                <div className="flex items-center pt-10 mx-1.5 shrink-0">
                  <div
                    className="w-5 h-[2px] rounded-full"
                    style={{ backgroundColor: prevBlocked ? "#e07070" : "#bcc8e0" }}
                  />
                  <div
                    className="w-0 h-0 -ml-px shrink-0"
                    style={{
                      borderTop: "4px solid transparent",
                      borderBottom: "4px solid transparent",
                      borderLeft: `6px solid ${prevBlocked ? "#e07070" : "#bcc8e0"}`,
                    }}
                  />
                </div>
              )}

              {/* Stage card */}
              <div className={`${nm.card} rounded-xl overflow-hidden w-[168px] shrink-0`}>
                {/* Colored top bar */}
                <div className="h-1 w-full" style={{ backgroundColor: stage.color }} />

                {/* Stage label */}
                <div className="px-3 pt-2 pb-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: stage.color }}>
                      {stage.label}
                    </p>
                    <span
                      className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: status === "has-data" ? "#d1fae5" : status === "empty" ? "#f3f4f6" : "#f3f4f6",
                        color: status === "has-data" ? "#059669" : "#9ca3af",
                      }}
                    >
                      {status === "has-data" ? "active" : status === "empty" ? "empty" : "—"}
                    </span>
                  </div>
                  <p className="text-[9px] text-[#6b7280] mt-0.5">{stage.description}</p>
                </div>

                {/* Divider */}
                <div className="mx-3 h-px bg-[#c4d0e8]" />

                {/* Tab rows */}
                <div className="p-1.5 space-y-1">
                  {stage.tabs.map((tab) => {
                    const Icon = tab.icon;
                    const count = tab.getCount ? tab.getCount(project) : null;
                    const hasData = count !== null && count > 0;
                    const isActive = activeTab === tab.id;

                    return (
                      <button
                        key={tab.id}
                        onClick={() => onNavigate(tab.id)}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-left transition-all ${
                          isActive
                            ? "shadow-[inset_3px_3px_7px_#bcc8e0,inset_-3px_-3px_7px_#ffffff]"
                            : "hover:shadow-[inset_2px_2px_5px_#bcc8e0,inset_-2px_-2px_5px_#ffffff]"
                        }`}
                        style={{ backgroundColor: "#e4ecfb" }}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div
                            className="h-1.5 w-1.5 rounded-full shrink-0"
                            style={{
                              backgroundColor: isActive
                                ? stage.color
                                : hasData
                                ? "#059669"
                                : count === 0
                                ? "#d1d5db"
                                : "#d1d5db",
                            }}
                          />
                          <Icon className="h-3 w-3 shrink-0" style={{ color: isActive ? stage.color : "#6b7280" }} />
                          <span
                            className="text-[10px] font-medium truncate"
                            style={{ color: isActive ? stage.color : "#374151" }}
                          >
                            {tab.label}
                          </span>
                        </div>
                        {count !== null && (
                          <span
                            className="text-[9px] font-mono shrink-0 px-1 py-0.5 rounded"
                            style={{
                              backgroundColor: hasData ? "#d1fae5" : "#f3f4f6",
                              color: hasData ? "#059669" : "#9ca3af",
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Support tools */}
      <div className={`${nm.inset} rounded-xl p-3 mt-4`}>
        <p className="text-[9px] font-semibold text-[#6b7280] uppercase tracking-widest mb-2.5">Support Tools</p>
        <div className="flex flex-wrap gap-2">
          {SUPPORT.map((tab) => {
            const Icon = tab.icon;
            const count = tab.getCount ? tab.getCount(project) : null;
            const hasData = count !== null && count > 0;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onNavigate(tab.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                  isActive
                    ? "shadow-[inset_3px_3px_7px_#bcc8e0,inset_-3px_-3px_7px_#ffffff] text-[#111827]"
                    : "shadow-[3px_3px_7px_#bcc8e0,-3px_-3px_7px_#ffffff] text-[#374151] hover:shadow-[inset_2px_2px_5px_#bcc8e0,inset_-2px_-2px_5px_#ffffff]"
                }`}
                style={{ backgroundColor: "#e4ecfb" }}
              >
                <div
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: hasData ? "#059669" : "#d1d5db" }}
                />
                <Icon className="h-3 w-3" style={{ color: isActive ? "#2563eb" : "#6b7280" }} />
                <span>{tab.label}</span>
                {count !== null && (
                  <span
                    className="text-[9px] font-mono"
                    style={{ color: hasData ? "#059669" : "#9ca3af" }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
